import MaximorDarkLogo from 'assets/brand/primary-logo.svg';
import { colors } from 'theme/colors';

import { User } from 'types/data/user';

export const USER_IMAGE_COLOR_PALETTE = [
  { color: colors.lime8, backgroundColor: colors.lime2 },
  { color: colors.cyan8, backgroundColor: colors.cyan2 },
  { color: colors.subtleMint8, backgroundColor: colors.subtleMint3 },
  { color: colors.ledgerWhite8, backgroundColor: colors.ledgerWhite4 },
];

// This is an unexpected user but shown gracefully in the UI.
export const UNKNOWN_USER_NAME = 'Maximor System';
export const UNKNOWN_USER_INITIALS = 'X';
export const SPECIAL_USER_INITIALS = 'MX';

export interface SpecialUserConfig {
  displayName: string;
  logoSrc: string;
  bgColor: string;
  className?: string;
}
export const SPECIAL_USER_CONFIG: Record<string, SpecialUserConfig> = {
  system: {
    displayName: 'Maximor',
    logoSrc: MaximorDarkLogo,
    bgColor: colors.colorWhite,
  },
  maxi_ai: {
    displayName: 'Maxi AI',
    logoSrc: MaximorDarkLogo,
    bgColor: colors.lime5,
    className: 'p-0.5',
  },
  support_user: {
    displayName: 'Maximor Support',
    logoSrc: MaximorDarkLogo,
    bgColor: colors.colorWhite,
  },
} as const;

export const getInitialsFromName = (name: string) => {
  const parts = name.trim().split(' ');
  const first = parts[0]?.[0]?.toUpperCase() ?? '';
  const last =
    parts.length > 1 ? (parts[parts.length - 1]?.[0]?.toUpperCase() ?? '') : '';
  return first + last || UNKNOWN_USER_INITIALS;
};

export const isSpecialUser = (userId: string) => userId in SPECIAL_USER_CONFIG;
export const isMaximorSupportUser = (name: string) =>
  name.split(' ')[0]?.toLowerCase() === 'maximor';

export const getName = (userId: string, usersMap: Record<string, User>) => {
  return isSpecialUser(userId)
    ? SPECIAL_USER_CONFIG[userId].displayName
    : (usersMap[userId]?.name ?? usersMap[userId]?.email ?? UNKNOWN_USER_NAME);
};

export const getAvatarConfig = (
  userId: string,
  usersMap: Record<string, User>
) => {
  const name = getName(userId, usersMap);
  const specialConfig = isSpecialUser(userId)
    ? SPECIAL_USER_CONFIG[userId]
    : isMaximorSupportUser(name)
      ? SPECIAL_USER_CONFIG.support_user
      : undefined;
  const initials = isSpecialUser(userId)
    ? SPECIAL_USER_INITIALS
    : userId in usersMap
      ? getInitialsFromName(name)
      : UNKNOWN_USER_INITIALS;

  const { color, bgColor } = getBgColor(userId, usersMap);
  return {
    color,
    bgColor,
    imageSrc: usersMap[userId || '']?.photoUrl || undefined,
    logoSrc: specialConfig?.logoSrc,
    className: specialConfig?.className,
    initials,
  };
};

export const getBgColor = (
  userId: string,
  usersMap: Record<string, User>
): { color: string; bgColor: string } => {
  const name = getName(userId, usersMap);
  if (isSpecialUser(userId)) {
    const config = SPECIAL_USER_CONFIG[userId];
    // Determine text color based on background color
    let textColor: string = colors.colorText;
    if (config.bgColor === colors.lime5) {
      textColor = colors.lime8 as string;
    }
    return { color: textColor, bgColor: config.bgColor };
  }
  if (isMaximorSupportUser(name)) {
    const config = SPECIAL_USER_CONFIG.support_user;
    return { color: colors.colorText as string, bgColor: config.bgColor };
  }
  if (!name) {
    const palette = USER_IMAGE_COLOR_PALETTE[0];
    return {
      color: palette.color as string,
      bgColor: palette.backgroundColor as string,
    };
  }
  const charCode = name.normalize('NFD')[0]?.charCodeAt(0) || 0;
  const index = charCode % USER_IMAGE_COLOR_PALETTE.length;
  const palette = USER_IMAGE_COLOR_PALETTE[index];
  return {
    color: palette.color as string,
    bgColor: palette.backgroundColor as string,
  };
};
